import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toolbar } from 'primereact/toolbar';
import { getDuplicateTicketCounts, getDuplicateTicketDetails, deleteTicket } from "./Integracion";
import './GestionTickets.scss';

const TicketManagement = () => {
    // States for data
    const [duplicateCounts, setDuplicateCounts] = useState([]);
    const [duplicateTickets, setDuplicateTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const today = new Date();
    const [dateRange, setDateRange] = useState({
        startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6),
        endDate: today
    });

    // States for edit/delete functionality
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [ticketDialog, setTicketDialog] = useState(false);
    const [editTicket, setEditTicket] = useState({
        ID: null,
        FOLIO_TICKET: null,
        FECHA: '',
        NOMBRE: null,
        UNIDAD: null
    });

    const toast = useRef(null);
    const dt = useRef(null);

    // Simulate fetching data from API
    const fetchDuplicateData = async () => {
        setLoading(true);

        try {
            const startDateStr = dateRange.startDate;
            const endDateStr = dateRange.endDate;
            const [countData, ticketData] = await Promise.all([
                getDuplicateTicketCounts(startDateStr, endDateStr),
                getDuplicateTicketDetails(startDateStr, endDateStr)
            ]);
            setDuplicateCounts(countData);
            setDuplicateTickets(ticketData);

        } catch (error) {
            console.error('Error fetching duplicate data:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'No se pudieron recuperar los datos. Por favor, inténtelo de nuevo.',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial data load
    useEffect(() => {
        fetchDuplicateData();
    }, []);

    // Refresh data when date range changes
    const refreshData = () => {
        fetchDuplicateData();
        toast.current.show({ severity: 'info', summary: 'Refreshed', detail: 'Datos actualizados', life: 3000 });
    };

    // Edit ticket handler
    const editTicketHandler = (ticket) => {
        setEditTicket({
            ID: ticket.ID,
            FOLIO_TICKET: ticket.FOLIO_TICKET,
            FECHA: new Date(ticket.FECHA),
            NOMBRE: ticket.NOMBRE,
            UNIDAD: ticket.UNIDAD,
        });
        setTicketDialog(true);
    };

    // Delete single ticket confirmation
    const confirmDeleteTicket = (ticket) => {
        confirmDialog({
            message: `¿Está seguro de que desea eliminar el ticket con folio "${ticket.FOLIO_TICKET}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteSelectedTickets([ticket]),
            reject: () => {}
        });
    };

    // Delete multiple tickets confirmation
    const confirmDeleteSelected = () => {
        if (!selectedTickets || selectedTickets.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'Warning', detail: 'No hay tickets seleccionados', life: 3000 });
            return;
        }

        confirmDialog({
            message: `¿Está seguro de que desea eliminar los ${selectedTickets.length} ticket(s)?`,
            header: 'Confirmar eliminación múltiple',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => deleteSelectedTickets(selectedTickets),
            reject: () => {}
        });
    };

    // Delete tickets
    const deleteSelectedTickets = (tickets) => {
        // In a real application, this would be an API call to delete the records
        const ticketIds = tickets.map(ticket => ticket.ID);
        const updatedTickets = duplicateTickets.filter(t => !ticketIds.includes(t.ID));
        setDuplicateTickets(updatedTickets);

        // Update count data
        const updatedCounts = [...duplicateCounts];

        // Group tickets by folioTicket to count how many of each are being deleted
        const deletedByFolio = tickets.reduce((acc, ticket) => {
            acc[ticket.FOLIO_TICKET] = (acc[ticket.FOLIO_TICKET] || 0) + 1;
            return acc;
        }, {});

        // Update or remove count entries
        for (let i = updatedCounts.length - 1; i >= 0; i--) {
            const count = updatedCounts[i];
            if (deletedByFolio[count.FOLIO_TICKET]) {
                const newTotal = count.totalRepetidos - deletedByFolio[count.FOLIO_TICKET];
                if (newTotal > 1) {
                    updatedCounts[i] = { ...count, TOTAL_REPETIDOS: newTotal };
                } else {
                    updatedCounts.splice(i, 1);
                }
            }
        }

        setDuplicateCounts(updatedCounts);
        deleteTicket(ticketIds).then(() => {
            setSelectedTickets([]);
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: `${tickets.length} Ticket(s) Eliminados`,
                life: 3000
            });
        })
    };

    // Save edited ticket
    const saveTicket = () => {
        // In a real application, this would be an API call to update the record
        const updatedTickets = duplicateTickets.map(t => (
            t.ID === editTicket.ID ? {
                ...t,
                FOLIO_TICKET: editTicket.FOLIO_TICKET,
                FECHA: editTicket.FECHA instanceof Date ? editTicket.FECHA.toISOString() : editTicket.FECHA,
                NOMBRE: editTicket.NOMBRE,
                UNIDAD: editTicket.UNIDAD,
            } : t
        ));

        setDuplicateTickets(updatedTickets);
        setTicketDialog(false);
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Ticket Actualizado', life: 3000 });
    };

    // Format date for display
    const formatDate = (value) => {
        if (!value) return '';
        const date = new Date(value);
        return date.toLocaleString();
    };

    // Date template
    const dateBodyTemplate = (rowData) => {
        return formatDate(rowData.FECHA);
    };

    // Template for action buttons
    const actionBodyTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                {/*<Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success edit-button"
                    onClick={() => editTicketHandler(rowData)}
                />*/}
                <Button
                    icon="pi pi-trash"
                    className="p-button-rounded p-button-danger delete-button"
                    onClick={() => confirmDeleteTicket(rowData)}
                />
            </div>
        );
    };

    // Footer for the ticket dialog
    const ticketDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setTicketDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveTicket} />
        </>
    );

    // Toolbar left content - Delete Selected button
    const leftToolbarTemplate = () => {
        return (
            <div className="toolbar-left">
                <Button
                    label="Eliminar Seleccionados"
                    icon="pi pi-trash"
                    className="p-button-danger"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedTickets || selectedTickets.length === 0}
                />
            </div>
        );
    };

    // Toolbar right content - Selection info
    const rightToolbarTemplate = () => {
        return (
            <div className="toolbar-right">
                {selectedTickets && selectedTickets.length > 0 && (
                    <span className="selection-info">
                        {selectedTickets.length} {selectedTickets.length === 1 ? 'ticket seleccionado' : 'tickets seleccionados'}
                    </span>
                )}
            </div>
        );
    };

    return (
        <div className="duplicate-tickets-view">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="header-section">
                <div className="date-filters">
                    <div className="date-range">
                        <label htmlFor="startDate">Fecha Inicio</label>
                        <Calendar
                            id="startDate"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({...dateRange, startDate: e.value})}
                            showIcon
                            dateFormat="dd/mm/yy"
                        />
                    </div>

                    <div className="date-range">
                        <label htmlFor="endDate">Fecha Fin</label>
                        <Calendar
                            id="endDate"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({...dateRange, endDate: e.value})}
                            showIcon
                            dateFormat="dd/mm/yy"
                        />
                    </div>

                    <Button
                        label="Buscar"
                        icon="pi pi-search"
                        onClick={refreshData}
                        className="filter-button"
                    />
                </div>
            </div>

            <TabView className="tickets-tabs">
                <TabPanel header="Tickets Duplicados">
                    <div className="card">
                        <DataTable
                            value={duplicateCounts}
                            dataKey="FOLIO_TICKET"
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25]}
                            className="duplicate-counts-table"
                            emptyMessage="No se encontraron tickets duplicados."
                            loading={loading}
                        >
                            <Column field="FOLIO_TICKET" header="Folio Ticket" sortable />
                            <Column field="TOTAL_REPETIDOS" header="Cantidad de veces duplicadas" sortable />
                        </DataTable>
                    </div>
                </TabPanel>

                <TabPanel header="Detalles De Tickets">
                    <div className="card">
                        <Toolbar className="datatable-toolbar" left={leftToolbarTemplate} right={rightToolbarTemplate} />
                        <DataTable
                            ref={dt}
                            value={duplicateTickets}
                            selection={selectedTickets}
                            onSelectionChange={(e) => setSelectedTickets(e.value)}
                            dataKey="ID"
                            paginator
                            rows={25}
                            rowsPerPageOptions={[25, 50, 100]}
                            scrollable
                            scrollHeight="calc(100vh - 415px)"
                            className="duplicate-tickets-table"
                            emptyMessage="No se encontraron tickets duplicados."
                            loading={loading}
                            responsiveLayout="scroll"
                            selectionMode="checkbox"
                        >
                            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                            <Column field="ID" header="ID" sortable />
                            <Column field="FOLIO_TICKET" header="Folio Ticket" sortable />
                            <Column field="ID_PARTIDA" header="ID Partida" sortable />
                            <Column field="PROVEEDOR" header="Proveedor" sortable />
                            <Column field="FECHA" header="Fecha" sortable />
                            <Column field="UNIDAD" header="Unidad" sortable />
                            <Column field="NOMBRE" header="Operador" sortable />
                            <Column field="KILOMETRAJE" header="Kilometraje" sortable />
                            <Column field="MONTO" header="Monto" sortable />
                            <Column body={actionBodyTemplate} header="Actions" />
                        </DataTable>
                    </div>
                </TabPanel>
            </TabView>

            <Dialog
                visible={ticketDialog}
                style={{ width: '450px' }}
                header="Edit Ticket"
                modal
                className="p-fluid"
                footer={ticketDialogFooter}
                onHide={() => setTicketDialog(false)}
            >
                <div className="field">
                    <label htmlFor="folioTicket">Folio Ticket</label>
                    <InputText
                        id="folioTicket"
                        value={editTicket.FOLIO_TICKET}
                        onChange={(e) => setEditTicket({ ...editTicket, FOLIO_TICKET: e.target.value })}
                        required
                        autoFocus
                    />
                </div>

                <div className="field">
                    <label htmlFor="fecha">Date</label>
                    <Calendar
                        id="fecha"
                        value={editTicket.FECHA}
                        onChange={(e) => setEditTicket({ ...editTicket, FECHA: e.value })}
                        showTime
                        hourFormat="24"
                        dateFormat="dd/mm/yy"
                    />
                </div>

                <div className="field">
                    <label htmlFor="idPartida">Nombre</label>
                    <InputText
                        id="idPartida"
                        value={editTicket.NOMBRE || ''}
                        onChange={(e) => setEditTicket({ ...editTicket, NOMBRE: e.target.value || null })}
                        placeholder="Leave empty for null value"
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default TicketManagement;